import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  SettingEditor,
  EpisodeOutlineList,
  SubjectSettingList,
  EpisodeOutlineForm,
  FailureRecoveryModal
} from '../components/projectSettings';
import { ProjectLayout, type NavItem } from '../components/projects';
import { useAssets } from '../hooks/useAssets';
import { useAutoGeneration } from '../hooks/useAutoGeneration';
import { useDialog } from '../hooks/useDialog';
import { projectSettingApi } from '../services/user/projectSetting.api';
import { episodeOutlineApi } from '../services/user/episodeOutline.api';
import { projectScriptApi } from '../services/user/projectScript.api';
import { useToast } from '../stores/toastStore';
import { useProjectStore } from '../stores/projectStore';
import {
  ProjectSettingType,
  IProjectSetting,
  IEpisodeOutline,
  CreateEpisodeOutlineRequest,
  TOTAL_GENERATION_STEPS
} from '@packages/global';

// 根据 URL 参数解析初始导航项
function parseNavFromUrl(nav: string | null): NavItem {
  if (!nav) return { type: 'setting', settingType: ProjectSettingType.STORY_SYNOPSIS };

  if (Object.values(ProjectSettingType).includes(nav as ProjectSettingType)) {
    return { type: 'setting', settingType: nav as ProjectSettingType };
  }

  if (nav === 'outline') return { type: 'outline' };
  if (nav === 'subject') return { type: 'subject' };
  if (nav === 'script') return { type: 'script' };

  return { type: 'setting', settingType: ProjectSettingType.STORY_SYNOPSIS };
}

export default function ProjectSettingsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm } = useDialog();
  const { loadProject } = useProjectStore();
  const { t } = useTranslation(['projectSettings', 'project', 'common']);

  const navParam = searchParams.get('nav');
  const [activeNavItem, setActiveNavItem] = useState<NavItem>(() => parseNavFromUrl(navParam));
  const [showFailureModal, setShowFailureModal] = useState(false);
  // 记录已访问过的 tab 类型，首次访问时挂载组件，之后保持挂载避免重复请求
  const [mountedTabs, setMountedTabs] = useState<Set<string>>(() => new Set([parseNavFromUrl(navParam).type]));

  // 自动生成 Hook
  const {
    state: autoGenState,
    isLoading: autoGenLoading,
    retry,
    cancel,
    switchToManualMode
  } = useAutoGeneration(projectId, {
    onProjectMetaUpdated: (title) => {
      // 项目信息更新回调 - 刷新项目数据以更新标题
      if (projectId) {
        loadProject(projectId);
      }
    },
    onStepCompleted: (stepType) => {
      // 步骤完成回调 - 按步骤类型有选择地刷新数据
      console.log('[ProjectSettingsPage] Step completed:', stepType);

      // 小说解析步骤完成时，刷新设定（故事梗概可能已生成）
      if (stepType === 'novel_parse') {
        loadSettings();
        if (projectId) {
          loadProject(projectId);
        }
      }

      // 设定类步骤完成时刷新设定
      const settingSteps = ['story_synopsis', 'worldview', 'writing_style', 'art_style', 'project_meta'];
      if (settingSteps.includes(stepType)) {
        loadSettings();
      }

      // 分集大纲步骤完成时刷新大纲
      if (stepType === 'episode_outline') {
        loadOutlines();
      }

      // 主体设定步骤完成时刷新主体
      if (stepType === 'subject') {
        loadSubjects();
      }

      // 剧本生成步骤完成时刷新项目数据
      if (stepType === 'episode_script' && projectId) {
        loadProject(projectId);
      }

      // 项目信息步骤完成时刷新项目数据
      if (stepType === 'project_meta' && projectId) {
        loadProject(projectId);
      }
    }
  });

  // 当失败时显示恢复弹窗
  useEffect(() => {
    if (autoGenState.failedStep && autoGenState.errorMessage) {
      setShowFailureModal(true);
    }
  }, [autoGenState.failedStep, autoGenState.errorMessage]);

  // 当 URL 参数变化时更新导航项
  useEffect(() => {
    if (navParam) {
      const navItem = parseNavFromUrl(navParam);
      setActiveNavItem(navItem);
      setMountedTabs(prev => {
        if (prev.has(navItem.type)) return prev;
        const next = new Set(prev);
        next.add(navItem.type);
        return next;
      });
      setSearchParams({}, { replace: true });
    }
  }, [navParam, setSearchParams]);

  // Settings data states
  const [settings, setSettings] = useState<Record<ProjectSettingType, IProjectSetting | null>>({
    [ProjectSettingType.WORLDVIEW]: null,
    [ProjectSettingType.WRITING_STYLE]: null,
    [ProjectSettingType.ART_STYLE]: null,
    [ProjectSettingType.STORY_SYNOPSIS]: null
  });
  const [outlines, setOutlines] = useState<IEpisodeOutline[]>([]);
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Project assets
  const { assets: subjects, loading: assetsLoading, loadAssets: loadSubjects } = useAssets({ projectId: projectId! });

  // Form modal states
  const [showOutlineForm, setShowOutlineForm] = useState(false);
  const [editingOutline, setEditingOutline] = useState<IEpisodeOutline | null>(null);

  useEffect(() => {
    if (projectId) {
      loadSettings();
      loadOutlines();
    }
  }, [projectId]);

  const loadSettings = useCallback(async () => {
    if (!projectId) return;
    try {
      setSettingsLoading(true);
      const settingsList = await projectSettingApi.getSettings(projectId);
      const settingsMap: Record<ProjectSettingType, IProjectSetting | null> = {
        [ProjectSettingType.WORLDVIEW]: null,
        [ProjectSettingType.WRITING_STYLE]: null,
        [ProjectSettingType.ART_STYLE]: null,
        [ProjectSettingType.STORY_SYNOPSIS]: null
      };
      settingsList.forEach(setting => {
        settingsMap[setting.settingType] = setting;
      });
      setSettings(settingsMap);
    } catch (error) {
      console.error('加载项目设定失败:', error);
    } finally {
      setSettingsLoading(false);
    }
  }, [projectId]);

  const loadOutlines = useCallback(async () => {
    if (!projectId) return;
    try {
      const outlinesList = await episodeOutlineApi.list(projectId);
      setOutlines(outlinesList);
    } catch (error) {
      console.error('加载分集大纲失败:', error);
    }
  }, [projectId]);

  // Settings handlers
  const handleSaveSetting = async (content: string) => {
    if (!projectId || activeNavItem.type !== 'setting') return;
    const settingType = activeNavItem.settingType;
    try {
      const updatedSetting = await projectSettingApi.updateSetting(projectId, settingType, { content });
      setSettings(prev => ({ ...prev, [settingType]: updatedSetting }));
      toast.success(t('toast.settingSaved'));
    } catch (error) {
      console.error('保存设定失败:', error);
      toast.error(t('toast.settingSaveFailed'));
      throw error;
    }
  };

  // Outline handlers
  const handleAddOutline = () => { setEditingOutline(null); setShowOutlineForm(true); };
  const handleEditOutline = (outline: IEpisodeOutline) => { setEditingOutline(outline); setShowOutlineForm(true); };

  const handleDeleteOutline = async (outline: IEpisodeOutline) => {
    const confirmed = await confirm(t('project:outline.deleteConfirm', { title: outline.title }), t('common:deleteConfirmTitle'), 'danger');
    if (!confirmed) return;
    try {
      await episodeOutlineApi.delete(outline.id);
      await loadOutlines();
      toast.success(t('toast.outlineDeleted'));
    } catch (error) {
      console.error('删除分集大纲失败:', error);
      toast.error(t('toast.outlineDeleteFailed'));
    }
  };

  const handleSubmitOutline = async (data: CreateEpisodeOutlineRequest) => {
    try {
      if (editingOutline) {
        await episodeOutlineApi.update(editingOutline.id, { episodeNumber: data.episodeNumber, title: data.title, synopsis: data.synopsis });
        toast.success(t('toast.outlineUpdated'));
      } else {
        await episodeOutlineApi.create(data);
        toast.success(t('toast.outlineCreated'));
      }
      setShowOutlineForm(false);
      await loadOutlines();
    } catch (error) {
      console.error('保存分集大纲失败:', error);
      toast.error(t('toast.outlineSaveFailed'));
      throw error;
    }
  };

  const handleGenerateScript = async (outline: IEpisodeOutline) => {
    if (!projectId) return;

    if (outline.generatedScriptId) {
      navigate(`/project/${projectId}/script?chapterId=${outline.generatedScriptId}`);
      return;
    }

    try {
      const existingScripts = await projectScriptApi.list(projectId);
      const nextChapterNumber = existingScripts.length > 0
        ? Math.max(...existingScripts.map(s => s.chapterNumber)) + 1
        : 1;

      const newChapter = await projectScriptApi.create({
        projectId,
        chapterNumber: nextChapterNumber,
        title: outline.title,
        content: ''
      });

      await episodeOutlineApi.update(outline.id, { generatedScriptId: newChapter.id });
      await loadOutlines();

      toast.success(t('toast.chapterCreatedRedirecting'));
      navigate(`/project/${projectId}/script?chapterId=${newChapter.id}&generate=true&outlineTitle=${encodeURIComponent(outline.title)}`);
    } catch (error) {
      console.error('创建章节失败:', error);
      toast.error(t('toast.chapterCreateFailed'));
    }
  };

  // 自动生成：处理取消
  const handleCancelAutoGen = async () => {
    try {
      await cancel();
      toast.info(t('toast.autoGenCancelled'));
    } catch (error: any) {
      toast.error(error.message || t('toast.autoGenCancelFailed'));
    }
  };

  // 自动生成：处理重试
  const handleRetry = async () => {
    if (!autoGenState.failedStep) return;
    try {
      await retry(autoGenState.failedStep);
      setShowFailureModal(false);
      toast.info(t('project:toast.retrying'));
    } catch (error: any) {
      toast.error(error.message || t('toast.autoGenRetryFailed'));
    }
  };

  // 自动生成：切换到手动模式
  const handleSwitchToManual = async () => {
    try {
      await switchToManualMode();
      setShowFailureModal(false);
      toast.success(t('project:toast.switchedToManual'));
      setSearchParams({}, { replace: true });
    } catch (error: any) {
      toast.error(error.message || t('toast.autoGenSwitchFailed'));
    }
  };

  // 切换导航时同时记录已访问的 tab
  const handleNavItemChange = useCallback((navItem: NavItem) => {
    setActiveNavItem(navItem);
    setMountedTabs(prev => {
      if (prev.has(navItem.type)) return prev;
      const next = new Set(prev);
      next.add(navItem.type);
      return next;
    });
  }, []);

  // 当前活跃的设定类型（仅在 setting tab 时有效）
  const activeSettingType = activeNavItem.type === 'setting' ? activeNavItem.settingType : null;

  // 稳定 artStyle 引用，避免 settings 对象更新时触发 SubjectSettingList 不必要的重渲染
  const projectArtStyle = useMemo(
    () => settings[ProjectSettingType.ART_STYLE]?.content || '',
    [settings[ProjectSettingType.ART_STYLE]?.content]
  );

  // 构建自动生成状态（传递给 ProjectLayout）
  const autoGenStatus = (autoGenState.isActive || autoGenState.completedSteps.length > 0) ? {
    isActive: autoGenState.isActive,
    currentStep: autoGenState.currentStep,
    completedSteps: autoGenState.completedSteps,
    failedStep: autoGenState.failedStep,
    totalSteps: TOTAL_GENERATION_STEPS,
    novelFileUrl: autoGenState.novelFileUrl  // 新增
  } : undefined;

  return (
    <ProjectLayout
      activeTab="settings"
      activeNavItem={activeNavItem}
      onNavItemChange={handleNavItemChange}
      autoGenStatus={autoGenStatus}
    >
      <section className="flex-1 p-6 overflow-auto">
        {/* 使用 hidden 隐藏非活跃 tab，避免切换时组件卸载重新请求数据 */}
        {/* mountedTabs 控制懒挂载：只在首次访问时挂载组件 */}
        {mountedTabs.has('setting') && (
          <div className={activeNavItem.type === 'setting' ? 'h-full' : 'hidden'}>
            <SettingEditor
              projectId={projectId!}
              settingType={activeSettingType || ProjectSettingType.STORY_SYNOPSIS}
              setting={settings[activeSettingType || ProjectSettingType.STORY_SYNOPSIS]}
              loading={settingsLoading}
              onSave={handleSaveSetting}
            />
          </div>
        )}
        {mountedTabs.has('outline') && (
          <div className={activeNavItem.type === 'outline' ? 'h-full' : 'hidden'}>
            <EpisodeOutlineList
              projectId={projectId!}
              outlines={outlines}
              loading={settingsLoading}
              onAdd={handleAddOutline}
              onEdit={handleEditOutline}
              onDelete={handleDeleteOutline}
              onGenerateScript={handleGenerateScript}
              onRefresh={loadOutlines}
            />
          </div>
        )}
        {mountedTabs.has('subject') && (
          <div className={activeNavItem.type === 'subject' ? 'h-full' : 'hidden'}>
            <SubjectSettingList
              projectId={projectId!}
              subjects={subjects}
              loading={assetsLoading}
              onRefresh={loadSubjects}
              projectArtStyle={projectArtStyle}
            />
          </div>
        )}
        {mountedTabs.has('script') && (
          <div className={activeNavItem.type === 'script' ? 'h-full' : 'hidden'}>
            <div className="h-full flex items-center justify-center">
              <button
                onClick={() => navigate(`/project/${projectId}/script`)}
                className="px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-colors cursor-pointer"
              >
                {t('viewEpisodeScripts')}
              </button>
            </div>
          </div>
        )}
      </section>

      <EpisodeOutlineForm
        isOpen={showOutlineForm}
        onClose={() => setShowOutlineForm(false)}
        onSubmit={handleSubmitOutline}
        projectId={projectId!}
        editingOutline={editingOutline}
        nextEpisodeNumber={outlines.length + 1}
      />

      {/* 失败恢复弹窗 */}
      {autoGenState.failedStep && (
        <FailureRecoveryModal
          isOpen={showFailureModal}
          onClose={() => setShowFailureModal(false)}
          failedStep={autoGenState.failedStep}
          errorMessage={autoGenState.errorMessage || t('project:unknownError')}
          onRetry={handleRetry}
          onSwitchToManual={handleSwitchToManual}
          isLoading={autoGenLoading}
        />
      )}
    </ProjectLayout>
  );
}
